import cx from 'classnames';
import { useContext } from 'react';

import { PageContext } from '../../context/PageContext';
import { WorkableListItemType } from '../../types';
import { Badge } from '../Badge/Badge';
import { Link } from '../buttons/Link';
import { IconLoader } from '../images/IconLoader';

export type JobsProps = {
  items?: WorkableListItemType[];
};

export const Jobs = ({ items }: JobsProps) => {
  const { sitemap, locale, config } = useContext(PageContext);
  const translations = config.translations;

  const jobOverviewPagePath: string = sitemap.find(({ _id }) => _id === 'page_jobs')
    ?.paths?.[locale];

  if (!Boolean(items?.filter(Boolean).length)) return null;

  return (
    <ul className="flex flex-col gap-3 md:gap-0">
      {items.map(
        ({ title, shortcode, slug, location, department, created_at }, index) => (
          <li
            key={shortcode || title}
            className={cx(
              'relative rounded-xl px-4 md:px-8 py-4 md:py-6 group',
              'transition-all hover:shadow-lg focus-within:shadow-lg duration-500',
              'shadow-lg md:shadow-none',
              'bg-white md:bg-transparent',
              'hover:bg-white focus-within:bg-white hover:z-10',
              'flex flex-col md:flex-row gap-2 md:gap-8 md:items-center',
              'md:-mt-0.5',
            )}
          >
            <div className="sm:w-3/5 md:w-1/2 flex flex-col-reverse md:flex-col gap-1 md:gap-3 pr-10 md:pr-16">
              {title && (
                <Link
                  href={`${jobOverviewPagePath}/${slug}`}
                  className="outline-none"
                >
                  <span className="absolute inset-0 bg-neutral-25 block opacity-0 z-10 cursor-pointer" />
                  <h3
                    className={cx(
                      'font-bold text-neutral-base text-title-sm-sm md:text-title-md-md lg:text-title-md-lg',
                      'group-hover:underline group-focus-within:underline underline-offset-4 md:group-hover:no-underline md:group-focus-within:no-underline',
                    )}
                  >
                    {title}

                    {isNew(created_at) && (
                      <span className="align-middle absolute right-4 top-0 md:relative md:right-auto md:top-auto md:ml-4 inline-flex">
                        <span className="absolute inset-x-0 h-4 bg-brand-light md:hidden" />
                        <span className="w-4 bg-white h-6 rounded-tr-sm relative z-10 md:hidden" />
                        <Badge
                          variant="white"
                          label="new"
                          className={cx(
                            'relative z-10',
                            'bg-brand-light text-brand-dark',
                            'md:bg-white md:text-brand-base',
                            'group-hover:bg-brand-light group-hover:text-brand-dark',
                          )}
                        />
                        <span className="w-4 bg-white h-6 rounded-tl-sm relative z-10 md:hidden" />
                      </span>
                    )}
                  </h3>
                </Link>
              )}

              {department && (
                <span className="font-bold text-brand-base text-sm md:text-title-sm-md lg:text-title-sm-lg">
                  {department}
                </span>
              )}
            </div>

            {location && (
              <p className="flex gap-2 text-neutral-25 font-bold text-md items-center">
                <IconLoader icon="map-marker" className="w-6 h-6" />{' '}
                {formatLocationLabel({
                  city: location?.city,
                  country: location?.country,
                  telecommuting: location?.telecommuting,
                  translations,
                  locale,
                })}
              </p>
            )}

            <div className="w-10 h-10 md:border-2 md:border-action-base text-action-base rounded-full flex items-center justify-center absolute right-4 md:right-8 top-1/2 -translate-y-1/2">
              <IconLoader icon="chevron" className="w-5 h-5 -rotate-90" />
            </div>

            {/* Trick to add bottom border. Can't use border due to box shadow and border radius */}
            {index !== items?.length - 1 && (
              <span className="hidden md:block h-0.5 bg-neutral-85 absolute left-0 right-0 bottom-0 group-hover:opacity-0 transition-opacity duration-500" />
            )}

            {/* Trick to 'hide' previous item bottom border when hovering. Can't use background color due to shadow and border radius */}
            <span className="hidden md:block h-0.5 bg-neutral-95 absolute left-0 right-0 top-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </li>
        ),
      )}
    </ul>
  );
};

function isNew(date: string): boolean {
  return new Date(date) > new Date(Date.now() - 1000 * 60 * 60 * 24 * 7);
}

function formatLocationLabel({
  city,
  country,
  telecommuting,
  translations,
  locale,
}): string {
  let label = [city, country].filter(Boolean).join(', ');
  if (label?.length && telecommuting) label += ' / ';
  if (telecommuting) label += translations?.full_remote?.[locale] || 'remote';
  return label;
}
